<template>
  <div class="container mx-auto p-10 border border-t-0">
    <!-- Basic User Info -->
    <div class="flex justify-between items-center mb-6">
        <h2 class="text-md">
          Donor Name: <span class="font-semibold">{{ donor?.user?.first_name }} {{ donor?.user?.last_name }}</span>
        </h2>
        <h2 class="text-md">
          Registered on: <span class="font-semibold">{{ new Date(donor?.created_at).toLocaleString() }}</span>
        </h2>
        <h2 class="text-md">
          Donor Code: <span class="font-semibold">{{ donor?.donor_code }}</span>
        </h2>
      </div>
    <h2 class="text-2xl font-bold mb-6">Process new donation</h2>

    <form class="grid grid-cols-1 md:grid-cols-2 gap-5" @submit.prevent="handleSubmit">
      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="donation_type">
          Select donation type <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <select id="donation_type" v-model="formData.donation_type"
            class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
            required>
            <option value="" disabled>Select Donation Type</option>
            <option value="" disabled selected>Select status</option>
              <option value="Replacement">
                Replacement (Replaces blood unit for specific patient)
              </option>
              <option value="Random">
                Random (First-time donor, not for specific patient)
              </option>
              <option value="Frequent">
                Regular-donor (Frequent donor, not for specific patient)
              </option>
              <option value="Designated">
                Designated/Directed (Donates for specific patient with doctor's
                letter)
              </option>
              <option value="Autologous">
                Autologous (Donates for future self use with doctor's letter)
              </option>
          </select>
        </div>
      </div>



      <!-- Conditionally show this field when "Replacement" is selected -->
      <div v-if="formData.donation_type === 'Replacement'" class="mt-4">
        <label class="block mb-2 text-gray-700 font-medium" for="ipop">
          Enter IP/OP Number/Name of the patient donor is replacing for
          <span class="text-red-500">*</span>
        </label>
        <input id="ipop" v-model="formData.ipop" type="text"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400"
          placeholder="Enter IP/OP Number or Patient Name" required />
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="pint_serial">Scan or type blood bag/pilot tube serial
          number<span class="text-red-500">*</span></label>
        <input id="pint_serial" v-model="formData.pint_serial" type="text"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Pint Serial Number" required>
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="technology">Select donation process <span
            class="text-red-500">*</span></label>
        <div class="relative">
          <select id="technology" v-model="formData.technology"
            class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
            required>
            <option value="" disabled>Select Donation Process</option>
            <option value="Normal">Voluntary/Regular</option>
            <option value="Apheresis">Apheresis</option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
      </div>

      <div v-if="formData.technology === 'Apheresis'">
        <label class="block mb-2 text-gray-700 font-medium" for="platelets_pre_count">
          Platelets Pre-Count</label>
        <input id="platelets_pre_count" v-model="formData.platelets_pre_count" type="number"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Platelets Pre-Count">
      </div>

      <div v-if="formData.technology === 'Apheresis'">
        <label class="block mb-2 text-gray-700 font-medium" for="red_blood_cells_pre_count">
          Red Blood Cells Pre-Count</label>
        <input id="red_blood_cells_pre_count" v-model="formData.red_blood_cells_pre_count" type="number"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Red Blood Cells Pre-Count">
      </div>

      <div v-if="formData.technology === 'Apheresis'">
        <label class="block mb-2 text-gray-700 font-medium" for="collection_device">
          Select Collection Device</label>
        <div class="relative">
          <select id="collection_device" v-model="formData.collection_device"
            class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none">
            <option value="">Select Collection Device</option>
            <option v-for="record in inventoryRecords" :key="record.id" :value="record.id">
              {{ record.item_name }} - {{ record.serial_number }}
            </option>
          </select>
        </div>
        <div v-if="!inventoryRecords || !inventoryRecords.length" class="text-yellow-600 text-sm mt-1">
          No collection devices found in inventory
        </div>
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="anticoagulants">
          Anticoagulants Used</label>
        <div class="relative">
          <input 
            id="anticoagulants" 
            v-model="formData.anticoagulants" 
            type="text"
            class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
            placeholder="Start typing anticoagulant name..."
            @input="onAnticoagulantInput"
            @focus="showAnticoagulantDropdown = true"
            @blur="hideAnticoagulantDropdown"
          >
          <ul v-if="showAnticoagulantDropdown && filteredAnticoagulants && filteredAnticoagulants.length > 0" 
            class="absolute z-50 w-full bg-white border border-gray-300 rounded-b shadow-lg max-h-48 overflow-y-auto mt-1"
            style="position: absolute; top: 100%;">
            <li v-for="(item, index) in filteredAnticoagulants" 
              :key="index"
              class="px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-700"
              @mousedown.prevent="selectAnticoagulant(item)">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>

      <div v-if="formData.technology === 'Apheresis'">
        <label class="block mb-2 text-gray-700 font-medium" for="anticoagulant_volume">
          Anticoagulant Volume (ml)</label>
        <input id="anticoagulant_volume" v-model="formData.anticoagulant_volume" type="number"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Anticoagulant Volume (ml)">
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="donation_duration">
          Donation Duration (minutes)</label>
        <input id="donation_duration" v-model="formData.donation_duration" type="number"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Duration (minutes)">
      </div>

      <div v-if="formData.technology === 'Apheresis'">
        <label class="block mb-2 text-gray-700 font-medium" for="quantity">
          Volume (ml)</label>
        <input id="quantity" v-model="formData.quantity" type="number"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          placeholder="Volume (ml)">
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="donation_successful">Was the donation successful? <span
            class="text-red-500">*</span></label>
        <div class="relative">
          <select id="donation_successful" v-model="formData.donation_successful"
            class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
            required>
            <option value="" disabled>Select Success Status</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
      </div>

      <div>
        <label class="block mb-2 text-gray-700 font-medium" for="date_added">Enter date and time of donation (yyyy-mm-dd
          H:m:s)<span class="text-red-500">*</span></label>
        <input id="date_added" v-model="formData.date_added" type="datetime-local"
          class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
          required>
      </div>
    </form>

    <!-- Disposables Section -->
    <div class="mt-8">
        <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Disposables</h3>
            <button type="button" class="px-3 py-1 border rounded text-custom-yellow border-custom-yellow text-sm" @click="addDisposable">
                + Add Disposable
            </button>
        </div>
        
        <div v-if="disposables && disposables.length > 0" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 border border-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase col-span-2">Name</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Batch Number</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Quantity</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date Time</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Procedure</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(disposable, index) in disposables" :key="index">
                        <td class="px-4 py-2 col-span-2">
                            <select v-model="disposable.inventory_record_id" 
                                class="w-full p-2 border rounded"
                                @change="onInventoryItemChange(index)">
                                <option value="">Select Item</option>
                                <option v-for="item in inventoryRecords" :key="item.id" :value="item.id">
                                    {{ item.item_name }}
                                </option>
                            </select>
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="disposable.batch_number" type="text" class="w-full p-2 border rounded" placeholder="Batch Number">
                        </td>
                        <td class="px-4 py-2">
                            <input v-model.number="disposable.quantity" type="number" class="w-full p-2 border rounded" placeholder="Qty">
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="disposable.date_time" type="datetime-local" class="w-full p-2 border rounded">
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="disposable.procedure" type="text" class="w-full p-2 border rounded" placeholder="Procedure">
                        </td>
                        <td class="px-4 py-2">
                            <button class="p-1 border rounded text-custom-yellow border-custom-yellow" @click="removeDisposable(index)">
                                <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                    fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                                </svg>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else-if="(!inventoryRecords || !inventoryRecords.length)" class="text-yellow-600 text-sm">
          No inventory items found. Please add items to inventory first.
        </div>
        <div v-else-if="!disposables || disposables.length === 0" class="text-gray-500 text-sm">No disposables added yet.</div>
    </div>

    <div class="mt-8 flex justify-between">
      <button class="px-4 py-2 border rounded text-orange-500 border-orange-500" @click="handleBack">Back</button>
      <div class="flex items-center space-x-4 mt-4 sm:mt-0">
        <CustomButton v-if="formData.donation_successful === 'No'" label="Defer"
          bg-class="bg-red-500 text-white px-8 py-3" @click="handleDeferral" />
        <CustomButton v-if="formData.donation_successful === 'No'" label="New Reaction"
          bg-class="bg-red-500 text-white px-8 py-3" @click="redirectToDonationReactionForm" />
        <CustomButton v-if="formData.donation_successful === 'Yes'" label="Register Donation"
          bg-class="bg-custom-yellow text-white px-8 py-3" @click="handleRecordDonation" />
      </div>
    </div>
  </div>
</template>

<script>
import CustomButton from '@/components/CustomButton.vue';
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';


export default {
  name: 'RecordDonationForm',
  components: {
    CustomButton,
  },
  props: {
    donorId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      facilityId: null,
      formData: {
        donation_type: '',
        pint_serial: '',
        technology: '',
        ipop: '',
        date_added: this.getCurrentDateTime(),
        donation_successful: '',
        platelets_pre_count: '',
        red_blood_cells_pre_count: '',
        collection_device: '',
        anticoagulants: '',
        quantity: '',
        donation_duration: '',
        added_by: null,
        facility_id: null,
      },
      inventoryRecords: [],
      anticoagulantsList: [],
      showAnticoagulantDropdown: false,
      disposables: [],
      donor: []
    };
  },

  computed: {
    userClaims() {
      return this.$store.getters['auth/claims'];
    },
    userData() {
      return this.$store.getters['auth/user'];
    },
  },

  created() {
        this.fetchDonorDetails();
        this.fetchInventoryRecords();
        this.fetchAnticoagulants();
        this.$nextTick(() => {
            this.formData.added_by = this.userData?.user_id;
            this.formData.facility_id = this.userData?.facility_id;
            this.formData.date_added = this.getCurrentDateTime();
        });
  },

  mounted() {
    this.facilityId = this.userData?.facility_id;
  },


  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },

    async fetchDonorDetails() {
        try {
            const donorId = this.donorId;
            const response = await api.getDonor(donorId);
            this.donor = response.data || response;
            if (this.donor.donation_status === "Replacement") {
                this.formData.donation_type = "Replacement";
            }
        } catch (error) {
            this.error = error.message || "Failed to fetch donor details";
            console.error("Error fetching donor details:", this.error);
        }
    },

    async fetchInventoryRecords() {
        try {
            const response = await api.getInventoryRecords();
            if (response && response.data) {
                this.inventoryRecords = response.data;
            } else if (Array.isArray(response)) {
                this.inventoryRecords = response;
            } else {
                this.inventoryRecords = [];
            }
        } catch (error) {
            console.error("Error fetching inventory records:", error);
            this.inventoryRecords = [];
        }
    },

    async fetchAnticoagulants() {
        // Anticoagulants are already in inventoryRecords, extract them
        const anticoagulants = (this.inventoryRecords || []).filter(record => 
            (record.item_type && record.item_type.toLowerCase().includes('anticoagulant')) ||
            (record.item_name && record.item_name.toLowerCase().includes('anticoagulant'))
        );
        this.anticoagulantsList = anticoagulants.map(r => r.item_name);
    },

    onAnticoagulantInput() {
        this.showAnticoagulantDropdown = true;
    },

    hideAnticoagulantDropdown() {
        setTimeout(() => {
            this.showAnticoagulantDropdown = false;
        }, 200);
    },

    selectAnticoagulant(item) {
        this.formData.anticoagulants = item;
        this.showAnticoagulantDropdown = false;
    },

    get filteredAnticoagulants() {
        if (!this.formData) return [];
        const search = (this.formData.anticoagulants || '').toLowerCase().trim();
        const list = this.anticoagulantsList || [];
        if (!search) return list.slice(0, 10);
        return list
            .filter(item => item.toLowerCase().includes(search))
            .slice(0, 10);
    },

    get deviceRecords() {
        if (!this.inventoryRecords || !Array.isArray(this.inventoryRecords)) return [];
        return this.inventoryRecords;
    },

    addDisposable() {
        this.disposables.push({
            inventory_record_id: '',
            batch_number: '',
            quantity: 1,
            date_time: this.getCurrentDateTime(),
            procedure: ''
        });
    },

    removeDisposable(index) {
        this.disposables.splice(index, 1);
    },

    async onInventoryItemChange(index) {
        const disposable = this.disposables[index];
        if (disposable.inventory_record_id) {
            const item = this.inventoryRecords.find(r => r.id === disposable.inventory_record_id);
            if (item) {
                disposable.batch_number = item.batch_number || '';
            }
        }
    },

    getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = ('0' + (now.getMonth() + 1)).slice(-2);
      const day = ('0' + now.getDate()).slice(-2);
      const hours = ('0' + now.getHours()).slice(-2);
      const minutes = ('0' + now.getMinutes()).slice(-2);
      const seconds = ('0' + now.getSeconds()).slice(-2);
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    },

    // Helper method to check for empty fields
    validateFields() {
      const emptyFields = [];
      if (!this.formData.donation_type) emptyFields.push('Donation Type');
      if (!this.formData.pint_serial) emptyFields.push('Pint Serial');
      if (!this.formData.technology) emptyFields.push('Donation Process');
      if (!this.formData.donation_successful) emptyFields.push('Success Status');
      if (!this.formData.date_added) emptyFields.push('Date Added');

      return emptyFields;
    },

    // Modified handleRecordDonation method
    async handleRecordDonation() {
      const emptyFields = this.validateFields();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      try {
        const response = await api.createBloodDonation(this.donorId, this.formData);
        console.log('Donation data saved successfully:', response);
        
        // Update donor vitals with platelets and RBC pre-count values (only for Apheresis)
        if (this.formData.technology === 'Apheresis' && (this.formData.platelets_pre_count || this.formData.red_blood_cells_pre_count)) {
          // Refresh donor details to get the most recent vital record
          const donorResponse = await api.getDonor(this.donorId);
          const updatedDonor = donorResponse.data || donorResponse;
          
          // Get today's date string for comparison
          const today = new Date().toISOString().split('T')[0];
          
          // Find the vital record created today
          const todayVitals = updatedDonor.donor_vitals.filter(vital => {
            const vitalDate = new Date(vital.created_at).toISOString().split('T')[0];
            return vitalDate === today;
          });
          
          if (todayVitals && todayVitals.length > 0) {
            // Sort by created_at to get the most recent one (latest timestamp)
            todayVitals.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            const todayVitalId = todayVitals[0].donor_vital_id;
            const vitalPayload = {
              platelets_pre_count: this.formData.platelets_pre_count || null,
              red_blood_cells_pre_count: this.formData.red_blood_cells_pre_count || null
            };
            await api.updateDonorVital(todayVitalId, vitalPayload);
            console.log('Donor vitals updated successfully for vital ID:', todayVitalId);
          }
        }
        
        // Save disposables if any
        if (this.disposables.length > 0) {
          const bloodDonationId = response.blood_donation_id;
          if (bloodDonationId) {
            try {
              const disposablesPayload = {
                donation_disposables: this.disposables.map(d => ({
                  inventory_record_id: d.inventory_record_id,
                  batch_number: d.batch_number,
                  quantity: d.quantity,
                  date_time: d.date_time,
                  procedure: d.procedure
                }))
              };
              await api.createDonationDisposables(bloodDonationId, disposablesPayload);
              console.log('Disposables saved successfully');
            } catch (disposableError) {
              console.error('Error saving disposables:', disposableError);
            }
          }
        }
        
        await SweetAlertService.showSuccess('Success', 'Donation saved successfully!.');
        this.$router.push({ name: 'DonorManagementPage' });
      } catch (error) {
        // console.error('Error submitting the form:', error.message); 
        SweetAlertService.showError('Error', error.message || 'An unknown error occurred.');
      }
    },

    // Modified handleDeferral method
    async handleDeferral() {
      try {
        SweetAlertService.showConfirmation('Continue to deferrals', 'Do you want to continue to the deferral form?')
          .then(async (result) => {
            if (result.isConfirmed) {
              const newDonorId = this.donorId;
              if (!newDonorId) {
                throw new Error('Missing donor_id in response');
              }

              // Proceed to deferral page
              this.$router.push({
                name: 'NewDonorDeferralForm',
                params: { donor_id: newDonorId }
              });
            }
          });
      } catch (error) {
        console.error('Error during deferral:', error);
        SweetAlertService.showError('Error', 'Something went wrong during the deferral process.');
      }
    },



    async redirectToDonationReactionForm() {
      try {
        SweetAlertService.showConfirmation('Continue to reactions', 'Do you want to continue to the reactions form?')
          .then(async (result) => {
            if (result.isConfirmed) {
              const newDonorId = this.donorId;
              if (!newDonorId) {
                throw new Error('Missing donor_id in response');
              }

              // Proceed to deferral page
              this.$router.push({
                name: 'NewReactionForm',
                params: { donor_id: newDonorId }
              });
            }
          });
      } catch (error) {
        console.error('Error during reactions:', error);
        SweetAlertService.showError('Error', 'Something went wrong during the reactions process.');
      }
    },

    handleBack() {
      this.$emit('move-to-previous-step');
    }
  },


}
</script>

<style scoped></style>